import React, { useState, useEffect, useCallback } from 'react';
import './TestimonialSection.scss';
import SvgIcons from '../../../../services/SvgIcons';
import { safeHtml } from '../../../../services/helpers';

const TestimonialSection = ({strapiData}) => {
    const [direction, setDirection] = useState('next');
    const [currentSlide, setCurrentSlide] = useState(0);
    const [exitSlide, setExitSlide] = useState(null);
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(null);

    // Modifier nextSlide pour définir la direction avant la transition
    const nextSlide = useCallback(() => {
        setDirection('next');
        setTimeout(() => {
            setExitSlide(currentSlide);
            setCurrentSlide((prev) => (prev === strapiData.Avis.length - 1 ? 0 : prev + 1));
        }, 0);
    }, [currentSlide, strapiData.Avis.length]);

    // Modifier prevSlide pour définir la direction avant la transition
    const prevSlide = useCallback(() => {
        setDirection('prev');
        setTimeout(() => {
            setExitSlide(currentSlide);
            setCurrentSlide((prev) => (prev === 0 ? strapiData.Avis.length - 1 : prev - 1));
        }, 0);
    }, [currentSlide, strapiData.Avis.length]);

    const goToSlide = (index) => {
        if (index !== currentSlide) {
            const newDirection = index > currentSlide ? 'next' : 'prev';
            setDirection(newDirection);
            setTimeout(() => {
                setExitSlide(currentSlide);
                setCurrentSlide(index);
            }, 0);
        }
    };

    // Pour le défilement automatique
    useEffect(() => {
        const timer = setInterval(() => {
            // Toujours définir la direction avant la transition
            setDirection('next');
            setTimeout(() => {
                setExitSlide(currentSlide);
                setCurrentSlide((prev) => (prev === strapiData.Avis.length - 1 ? 0 : prev + 1));
            }, 0);
        }, 5000);

        return () => clearInterval(timer);
    }, [currentSlide, strapiData.Avis.length]);

    // Nettoyage de l'état exitSlide après la transition
    useEffect(() => {
        if (exitSlide !== null) {
            const timer = setTimeout(() => {
                setExitSlide(null);
            }, 500); // Durée de la transition
            return () => clearTimeout(timer);
        }
    }, [exitSlide]);


    // Minimum de distance à parcourir pour considérer que c'est un swipe
    const minSwipeDistance = 50;

    // Gestion tactile
    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.touches[0].clientX);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.touches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            nextSlide();
        } else if (isRightSwipe) {
            prevSlide();
        }
    };

    // Gestion souris
    const onMouseDown = (e) => {
        setIsDragging(true);
        setStartX(e.clientX);
    };

    const onMouseMove = (e) => {
        if (!isDragging) return;
        setTouchEnd(e.clientX);
    };

    const onMouseUp = () => {
        if (!isDragging) return;
        
        if (startX && touchEnd) {
            const distance = startX - touchEnd;
            const isLeftSwipe = distance > minSwipeDistance;
            const isRightSwipe = distance < -minSwipeDistance;

            if (isLeftSwipe) {
                nextSlide();
            } else if (isRightSwipe) {
                prevSlide();
            }
        }
        
        setIsDragging(false);
        setStartX(null);
        setTouchEnd(null);
    };

    // Gestion de la sortie de la zone
    const onMouseLeave = () => {
        if (isDragging) {
            setIsDragging(false);
            setStartX(null);
            setTouchEnd(null);
        }
    };
    
    return (
        <section className="testimonial-section">
            <div className="testimonial-section__container">
                <button 
                    className="testimonial-section__nav testimonial-section__nav--prev" 
                    onClick={prevSlide}
                    aria-label="Témoignage précédent"
                >
                    <SvgIcons
                        name={'chevron_back'}
                        width={50}
                        height={50}
                        color={'#6D4F47'}
                        marginLeft={10}
                        transform={'rotate(0deg)'}
                    />
                </button>

                <div className="testimonial-section__content"
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                    onMouseDown={onMouseDown}
                    onMouseMove={onMouseMove}
                    onMouseUp={onMouseUp}
                    onMouseLeave={onMouseLeave}
                >
                    {strapiData.Avis.map((review, index) => (
                        <div
                            key={review.id}
                            className={`testimonial-section__slide
                            ${index === currentSlide ? 'active' : ''}
                            ${index === exitSlide ? 'exit' : ''}
                            ${direction === 'next' ? 'slide-next' : 'slide-prev'}`}
                            aria-hidden={index !== currentSlide}
                        >
                            <h2 className="testimonial-section__name">{review.Prénom}</h2>
                            <div className="testimonial-section__quote" dangerouslySetInnerHTML={safeHtml(review.Texte)} />
                        </div>
                    ))}
                </div>

                <button 
                    className="testimonial-section__nav testimonial-section__nav--next"
                    onClick={nextSlide}
                    aria-label="Témoignage suivant"
                >
                    <SvgIcons
                        name={'chevron_back'}
                        width={50}
                        height={50}
                        color={'#6D4F47'}
                        marginLeft={10}
                        transform={'rotate(180deg)'}
                    />
                </button>
            </div>

            <div className="testimonial-section__dots">
                {strapiData.Avis.map((_, index) => (
                    <button
                        key={index}
                        className={`testimonial-section__dot ${
                            index === currentSlide ? 'active' : ''
                        }`}
                        onClick={() => goToSlide(index)}
                        aria-label={`Aller au témoignage ${index + 1}`}
                    />
                ))}
            </div>
        </section>
    );
};

export default TestimonialSection;